import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "@/components/Icon";
import { Screen } from "@/components/Layout";
import { Spinner } from "@/components/Controls";
import { colors, spacing, radius, fonts, type } from "@/theme";
import { fetchVoiceSettings, saveVoiceSettings, fetchTts, MOODS, matchMood } from "@/api";
import { playBase64Mp3, stopAudio } from "@/lib/audioPlayer";

const DESCRIPTIONS = {
  gentle: "Soft and soothing, with a little warmth.",
  cheerful: "Brighter, livelier and more expressive.",
  calm: "Steady, slow and reassuring.",
};

export default function VoiceSetup() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [selected, setSelected] = useState("calm");
  const [previewLoading, setPreviewLoading] = useState(false);
  const previewTimer = useRef(null);

  useEffect(() => {
    (async () => {
      const v = await fetchVoiceSettings();
      if (!v?.elevenlabs_enabled) {
        navigate("/home", { replace: true });
        return;
      }
      const m = matchMood(v);
      setSelected(m?.key ?? "calm");
      setReady(true);
    })();
    return () => {
      stopAudio();
      if (previewTimer.current) clearTimeout(previewTimer.current);
    };
  }, [navigate]);

  const pick = (key) => {
    setSelected(key);
    const mood = MOODS.find((m) => m.key === key);
    if (!mood) return;
    saveVoiceSettings(mood.values);
    stopAudio();
    if (previewTimer.current) clearTimeout(previewTimer.current);
    setPreviewLoading(true);
    previewTimer.current = setTimeout(async () => {
      await saveVoiceSettings(mood.values);
      const audio = await fetchTts("Hello, it's so good to hear from you. I'm right here whenever you'd like to talk.");
      if (audio) playBase64Mp3(audio);
      setPreviewLoading(false);
    }, 400);
  };

  const cont = async () => {
    stopAudio();
    const mood = MOODS.find((m) => m.key === selected);
    if (mood) await saveVoiceSettings(mood.values);
    navigate("/home", { replace: true });
  };

  if (!ready) {
    return <Screen><div style={center}><Spinner size={40} /></div></Screen>;
  }

  return (
    <Screen testID="voice-setup-screen">
      <div style={{ padding: `${spacing.xl}px ${spacing.xl}px 160px` }}>
        <div style={badge}>
          <Icon name="users" size={16} color={colors.brand} />
          <span style={{ ...fonts.textBold, fontSize: type.base, color: colors.brand }}>For the family member</span>
        </div>
        <h1 data-testid="voice-setup-heading" style={{ ...fonts.displayBold, fontSize: type["3xl"], color: colors.onSurface, margin: `0 0 ${spacing.md}px` }}>Choose a voice</h1>
        <p style={{ ...fonts.text, fontSize: type.lg, color: colors.muted, lineHeight: 1.5, marginBottom: spacing.xl }}>
          Pick how the companion sounds, then tap to hear a sample. You can change this anytime in Settings.
        </p>

        {MOODS.map((mood) => {
          const active = selected === mood.key;
          return (
            <button key={mood.key} data-testid={`voice-setup-mood-${mood.key}`} onClick={() => pick(mood.key)}
              className="ember-press"
              style={{
                display: "flex", alignItems: "center", gap: spacing.md, width: "100%", textAlign: "left",
                background: active ? colors.brandPrimary : colors.surfaceSecondary, borderRadius: radius.lg,
                border: `2px solid ${active ? colors.brandPrimary : colors.border}`, padding: spacing.lg,
                marginBottom: spacing.md, minHeight: 88,
              }}>
              <div style={{ width: 56, height: 56, borderRadius: 28, background: active ? colors.brandSecondary : colors.brandTertiary, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Icon name={mood.icon} size={26} color={active ? colors.onBrandPrimary : colors.brand} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ ...fonts.textExtraBold, fontSize: type.xl, color: active ? colors.onBrandPrimary : colors.onSurface }}>{mood.label}</div>
                <div style={{ ...fonts.text, fontSize: type.base, color: active ? colors.brandTertiary : colors.muted, marginTop: 2 }}>{DESCRIPTIONS[mood.key]}</div>
              </div>
              {active && <Icon name={previewLoading ? "volume-2" : "check-circle"} size={24} color={colors.onBrandPrimary} />}
            </button>
          );
        })}

        <p style={{ ...fonts.textSemiBold, fontSize: type.base, color: colors.muted, textAlign: "center", marginTop: spacing.md }}>
          {previewLoading ? "Playing a sample…" : "Tap a voice above to hear it."}
        </p>
      </div>

      <div style={{ position: "sticky", bottom: 0, padding: `${spacing.md}px ${spacing.xl}px ${spacing.xl}px`, background: colors.surface }}>
        <button data-testid="voice-setup-continue" onClick={cont} className="ember-press" style={ctaStyle}>
          <span style={{ ...fonts.textExtraBold, fontSize: type.xl, color: colors.onBrandPrimary }}>Set this voice</span>
          <Icon name="arrow-right" size={26} color={colors.onBrandPrimary} />
        </button>
      </div>
    </Screen>
  );
}

const center = { display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" };
const badge = { display: "inline-flex", alignItems: "center", gap: spacing.sm, background: colors.surfaceSecondary, borderRadius: radius.pill, padding: `${spacing.sm}px ${spacing.md}px`, marginBottom: spacing.lg };
const ctaStyle = { width: "100%", minHeight: 76, borderRadius: radius.lg, background: colors.brandPrimary, display: "flex", alignItems: "center", justifyContent: "center", gap: spacing.md };
